import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { taxProfileMessages, vatFieldMessages } from '../i18n/messages';
import { TaxProfile, TTaxProfile, isTaxProfileRequiresVat } from '../model/taxProfile';

const VAT_PATTERN = /^(GB)?\d{9,12}$/;

const useGBTaxProfileOptions = () => {
  const intl = useIntl();

  return useMemo(
    () =>
      Object.values(TaxProfile).map(value => ({
        title: intl.formatMessage(taxProfileMessages[value]),
        value,
      })),
    [intl],
  );
};

const useGBVatFieldConfig = (isVatRequired: boolean) => {
  const intl = useIntl();

  const rules = useMemo(
    () => ({
      required: isVatRequired ? intl.formatMessage(vatFieldMessages.requiredError) : false,
      pattern: {
        value: VAT_PATTERN,
        message: intl.formatMessage(vatFieldMessages.patternError),
      },
    }),
    [intl, isVatRequired],
  );

  const description = useMemo(
    () =>
      intl.formatMessage(
        isVatRequired ? vatFieldMessages.descriptionRequired : vatFieldMessages.descriptionOptional,
      ),
    [intl, isVatRequired],
  );

  return { rules, description };
};

const useGBTaxProfile = (selectedTaxProfile: TTaxProfile | null | undefined) => {
  const isVatRequired = isTaxProfileRequiresVat(selectedTaxProfile);
  const taxProfileOptions = useGBTaxProfileOptions();
  const vatFieldConfig = useGBVatFieldConfig(isVatRequired);

  return {
    taxProfileOptions,
    isVatRequired,
    vatValidationRules: vatFieldConfig.rules,
    vatDescription: vatFieldConfig.description,
  };
};

export { useGBTaxProfileOptions, useGBVatFieldConfig, useGBTaxProfile };
